import { useRef } from "react";
import Barcode from "react-barcode";
import { isSunmiDevice, printOnSunmi } from "../../../utils/sunmiPrinter";
import { toast } from "../../../components/toast";

interface TransactionItem {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  vat?: number;
  total: number;
  isBogoFreeItem?: boolean;
}

interface TransactionDetailsModalProps {
  onClose: () => void;
  transaction: {
    id: string;
    time: string;
    date: string;
    amount: string;
    items: number;
    cashier: string;
    customer?: string;
    paymentMethod: string;
    paymentMethods?: Array<{ method: string; amount: number }>;
    subtotal: number;
    tax: number;
    discount: number;
    total: number;
    itemsList: TransactionItem[];
  };
}

export default function TransactionDetailsModal({
  onClose,
  transaction,
}: TransactionDetailsModalProps) {
  const receiptRef = useRef<HTMLDivElement>(null);

  const totalLineDiscount = transaction.itemsList.reduce(
    (sum, item) => sum + Number(item.discount || 0),
    0,
  );
  const totalLineVat = transaction.itemsList.reduce(
    (sum, item) => sum + Number(item.vat || 0),
    0,
  );
  const totalDiscount =
    transaction.discount > 0 ? transaction.discount : totalLineDiscount;
  const effectiveVat = transaction.tax > 0 ? transaction.tax : totalLineVat;

  const counterInfo = JSON.parse(
    localStorage.getItem("counterCheckedInInfo") || "{}",
  );
  const businessDetails = (() => {
    try { return JSON.parse(localStorage.getItem("businessDetails") || "{}"); } catch { return {}; }
  })();
  const bizData = businessDetails?.data ?? businessDetails;
  const logoUrl = bizData?.imagePath ?? null;
  const bizPhone = bizData?.phoneNumber ?? null;
  const bizCurrency = bizData?.currency?.isO_Code ?? "BDT";
  const bizAddress = (() => {
    const a = bizData?.addressDetail;
    if (!a) return "";
    return [a.addressLine1, a.addressLine2, a.city, a.district, a.division, a.country?.name]
      .filter((p) => p && p !== "string" && p !== "null")
      .join(", ");
  })();
  const outlet = (() => {
    try { return JSON.parse(localStorage.getItem("outlet") || "{}"); } catch { return {}; }
  })();

  const outletName = counterInfo?.outletName ?? "";
  const storeAddress = bizAddress || outlet?.address || outlet?.location || "";
  const storePhone =
    bizPhone ??
    outlet?.phone ??
    outlet?.phoneNumber ??
    outlet?.contactNumber ??
    outlet?.mobileNumber ??
    null;
  const bizFacebook =
    bizData?.facebookPage ??
    bizData?.facebookPageUrl ??
    bizData?.fbPage ??
    bizData?.socialMedia ??
    bizData?.website ??
    bizData?.websiteUrl ??
    outlet?.facebookPage ??
    outlet?.website ??
    null;
  const storeName = "Golfer";

  const paymentMethods = transaction.paymentMethods ?? [
    { method: transaction.paymentMethod, amount: transaction.total },
  ];
  const totalPaid = paymentMethods.reduce((sum, p) => sum + p.amount, 0);
  const change = totalPaid > transaction.total ? totalPaid - transaction.total : 0;

  const orderNo = transaction.id;
  const dateShort = transaction.date;
  const timeStr = transaction.time;
  const cashier = transaction.cashier;
  const customerName = transaction.customer || "Walk In Customer";

  const lineBreakdown = transaction.itemsList.map((item) => ({
    name: item.name,
    code: item.sku,
    price: item.unitPrice,
    qty: item.quantity,
    total: item.total,
    isBogoFreeItem: item.isBogoFreeItem || false,
    lineDiscount: Number(item.discount || 0),
    lineVat: Number(item.vat || 0),
  }));

  const totalQty = transaction.itemsList.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0,
  );
  const subtotal = transaction.subtotal;
  const total = transaction.total;

  const netPayable = subtotal - totalDiscount + effectiveVat;
  const rounding = total - netPayable;
  const showNetPayable = totalDiscount > 0 || effectiveVat > 0;
  const showRounding = Math.abs(rounding) >= 0.005;

  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const blk = { color: "#000" };

  const handlePrint = async () => {
    if (isSunmiDevice()) {
      if (!receiptRef.current) return;
      if (!window.SunmiPrinter?.isConnected()) {
        toast.error(
          "Printer not ready. Please check the printer and try again.",
        );
        return;
      }
      try {
        await printOnSunmi(receiptRef.current);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Print failed");
      }
      return;
    }

    const BLK = "color:#000";

    const itemsHtml = lineBreakdown
      .map(
        (item, idx) => `
        <tr>
          <td style="padding:3px 2px 3px 0;vertical-align:top;font-size:10px;font-weight:bold;${BLK}">${idx + 1}</td>
          <td style="padding:3px 2px;vertical-align:top;${BLK}">
            <div style="font-size:10px;${BLK}">${item.isBogoFreeItem ? "(Promo) " : ""}${item.name}</div>
            ${item.code ? `<div style="font-size:9px;${BLK}">SKU: ${item.code}</div>` : ""}
          </td>
          <td style="text-align:right;white-space:nowrap;padding:3px 2px;vertical-align:top;font-size:10px;${BLK}">${fmt(item.price)}</td>
          <td style="text-align:center;padding:3px 2px;vertical-align:top;font-size:10px;${BLK}">${item.qty}</td>
          <td style="text-align:right;white-space:nowrap;padding:3px 0 3px 2px;vertical-align:top;font-size:10px;${BLK}">${fmt(item.total)}</td>
        </tr>`,
      )
      .join("");

    const paymentBreakdownHtml = paymentMethods
      .map(
        (pm) =>
          `<tr><td style="padding:2px 0;padding-left:55px;font-size:11px;${BLK}">Paid (${pm.method})</td><td style="text-align:right;padding:2px 0;font-size:11px;${BLK}">${bizCurrency} ${fmt(pm.amount)}</td></tr>`,
      )
      .join("");

    const transactionDetailsHtml = paymentMethods
      .filter((pm) => pm.amount > 0)
      .map(
        (pm, idx) =>
          `<tr style="border-bottom:1px solid #ccc"><td style="padding:2px 0;font-size:10px;${BLK}">${idx + 1}. ${pm.method.toUpperCase()}</td><td style="text-align:right;padding:2px 0;font-size:10px;${BLK}">${bizCurrency} ${fmt(pm.amount)}</td></tr>`,
      )
      .join("");

    const effectiveLogo = logoUrl ?? `${window.location.origin}/golfer_logo.jpg`;

    const receiptHtml = `
      <div style="font-family:'Arial','Helvetica Neue',sans-serif;font-size:11px;line-height:1.5;max-width:300px;padding:0 8px;${BLK}">
        <div style="text-align:center;margin:0;padding:0">
          <div style="overflow:hidden;height:65px;display:flex;align-items:center;justify-content:center">
            <img src="${effectiveLogo}" alt="Golfer" style="width:280px;height:200px;object-fit:contain;display:block;flex-shrink:0" />
          </div>
        </div>
        ${outletName ? `<div style="text-align:center;font-size:11px;margin:2px 0;color:#666">Outlet Name: ${outletName}</div><div style="border-bottom:1px dashed #ccc;margin:4px 0"></div>` : ""}

        <table style="width:100%;border-collapse:collapse;font-size:10px;${BLK}">
          <tr>
            <td style="${BLK}">User: ${cashier}</td>
            <td style="text-align:right;white-space:nowrap;${BLK}">Inv. No.: ${orderNo}</td>
          </tr>
          <tr>
            <td style="${BLK}">Date: ${dateShort}</td>
            <td style="text-align:right;${BLK}">${timeStr}</td>
          </tr>
        </table>
        <div style="font-size:10px;${BLK}">Total number of Product: ${lineBreakdown.length}</div>
        <div style="font-size:10px;${BLK}">Total quantity of Product: ${totalQty}</div>
        <div style="height:2px;background:#000;margin:4px 0"></div>

        <table style="width:100%;border-collapse:collapse;table-layout:fixed;${BLK}">
          <colgroup>
            <col style="width:16px"/>
            <col/>
            <col style="width:62px"/>
            <col style="width:22px"/>
            <col style="width:64px"/>
          </colgroup>
          <thead>
            <tr style="border-top:1px solid #000;border-bottom:1px solid #000">
              <th style="text-align:left;padding:3px 2px 3px 0;font-size:10px;font-weight:bold;${BLK}">Sl.</th>
              <th style="text-align:left;padding:3px 2px;font-size:10px;font-weight:bold;${BLK}">Product</th>
              <th style="text-align:right;padding:3px 2px;font-size:10px;font-weight:bold;${BLK}">Price</th>
              <th style="text-align:center;padding:3px 2px;font-size:10px;font-weight:bold;${BLK}">Qty</th>
              <th style="text-align:right;padding:3px 0 3px 2px;font-size:10px;font-weight:bold;${BLK}">Amount</th>
            </tr>
          </thead>
          <tbody>${itemsHtml}</tbody>
        </table>
        <div style="height:2px;background:#000;margin:4px 0"></div>

        <table style="width:100%;border-collapse:collapse;font-size:11px;font-weight:bold;${BLK}">
          <tr><td style="padding:2px 0;padding-left:55px;${BLK}">Sub Total</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(subtotal)}</td></tr>
          <tr style="font-weight:normal"><td style="padding:2px 0;padding-left:55px;${BLK}">(-) Discount</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(totalDiscount)}</td></tr>
          <tr style="font-weight:normal"><td style="padding:2px 0;padding-left:55px;${BLK}">(+) VAT</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(effectiveVat)}</td></tr>
          ${showNetPayable ? `<tr><td style="padding:2px 0;padding-left:55px;${BLK}">Net Payable</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(netPayable)}</td></tr>` : ""}
          ${showRounding ? `<tr><td style="padding:2px 0;padding-left:55px;${BLK}">Rounding (+/-)</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(rounding)}</td></tr>` : ""}
        </table>
        <div style="border-top:1px dashed #ccc;border-bottom:2px solid #000;margin:4px 0;padding:3px 0">
          <table style="width:100%;border-collapse:collapse;${BLK}">
            <tr>
              <td style="font-weight:bold;font-size:13px;padding-left:55px;${BLK}">NET DUE</td>
              <td style="text-align:right;font-weight:bold;font-size:13px;${BLK}">${bizCurrency} ${fmt(total)}</td>
            </tr>
          </table>
        </div>

        <div style="font-weight:bold;font-size:11px;margin:6px 0 2px;${BLK}">PAYMENT BREAKDOWN</div>
        <table style="width:100%;border-collapse:collapse;${BLK}">
          ${paymentBreakdownHtml}
          <tr style="border-top:1px dashed #ccc"><td style="padding:2px 0;padding-left:55px;font-size:11px;font-weight:bold;${BLK}">Change</td><td style="text-align:right;padding:2px 0;font-size:11px;font-weight:bold;${BLK}">${bizCurrency} ${fmt(change)}</td></tr>
        </table>
        <div style="height:2px;background:#000;margin:4px 0"></div>

        <div style="font-weight:bold;font-size:11px;margin-bottom:2px;${BLK}">Transaction Details</div>
        <table style="width:100%;border-collapse:collapse;${BLK}">
          ${transactionDetailsHtml}
        </table>

        <table style="width:100%;border-collapse:collapse;font-size:11px;${BLK}">
          <tr>
            <td style="font-weight:bold;padding:2px 0;${BLK}">PAYMENT TOTAL</td>
            <td style="text-align:right;font-weight:bold;padding:2px 0;${BLK}">${bizCurrency} ${fmt(totalPaid)}</td>
          </tr>
        </table>
        <div style="height:2px;background:#000;margin:4px 0"></div>

        <div style="font-size:10px;${BLK}">Customer: ${customerName}</div>
        <div style="border-bottom:1px dashed #ccc;margin:4px 0"></div>

        <div style="font-weight:bold;font-size:10px;margin-bottom:2px;${BLK}">Terms &amp; Conditions:</div>
        <div style="font-size:9px;${BLK}">1. Products exchangeable within 15 days of purchase.</div>
        <div style="font-size:9px;${BLK}">2. Exchanged products cannot be re-exchanged.</div>
        <div style="font-size:9px;${BLK}">3. No refund in any form is allowed.</div>
        <div style="border-bottom:1px dashed #ccc;margin:4px 0"></div>

        <div style="text-align:center;margin:8px 0 2px">
          <div style="font-size:9px;margin-bottom:4px;${BLK}">Scan for Exchange / Tracking</div>
          <canvas id="barcode-receipt"></canvas>
          <div style="font-size:9px;margin-top:2px;${BLK}">${orderNo}</div>
        </div>
        <div style="height:2px;background:#000;margin:4px 0"></div>

        <div style="text-align:center;font-size:9px;margin-top:4px;line-height:1.7;${BLK}">
          ${storeAddress ? `<div style="${BLK}">${storeAddress}</div>` : ""}
          ${storePhone ? `<div style="${BLK}">Tel: ${storePhone}</div>` : ""}
          ${bizFacebook ? `<div style="${BLK}">${bizFacebook}</div>` : ""}
          <div style="margin-top:3px;${BLK}">
            <div style="${BLK}">Thank you for shopping with ${storeName}!</div>
            <div style="font-weight:bold;${BLK}">Customer Copy</div>
          </div>
        </div>
        <div style="text-align:right;font-size:9px;margin-top:6px;${BLK}">Powered by biznify.net</div>
      </div>`;

    const html = `<!DOCTYPE html><html><head>
      <title>Receipt - ${orderNo}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;color:#000}
        html,body{background:#fff;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{font-family:'Arial','Helvetica Neue',sans-serif;font-size:11px}
        .page{max-width:300px;margin:0 auto;padding:12px}
        @media print{
          *{color:#000 !important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          body{margin:0;padding:0}
          .page{padding:4px;max-width:100%}
        }
      </style>
    </head>
    <body>
      <div class="page">${receiptHtml}</div>
      <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></` + `script>
      <script>
        window.onload = function() {
          if(typeof JsBarcode !== 'undefined') {
            document.querySelectorAll('canvas[id^="barcode-"]').forEach(function(el) {
              try { JsBarcode(el, '${orderNo}', {format:'CODE128',width:2,height:55,displayValue:false,margin:0,lineColor:'#000',background:'#fff'}); } catch(e){}
            });
          }
          setTimeout(function(){ window.print(); window.close(); }, 500);
        };
      </` + `script>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] min-h-[300px] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <i className="ri-file-list-3-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">
              Transaction Details
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-teal-700 mb-1">Transaction ID</p>
                <p className="text-2xl font-bold text-teal-900">
                  {transaction.id}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-teal-700 mb-1">Total Amount</p>
                <p className="text-3xl font-bold text-teal-900">
                  {transaction.amount}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-teal-700 mb-1">Date &amp; Time</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.date} at {transaction.time}
                </p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Payment Method</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.paymentMethod}
                </p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Cashier</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.cashier}
                </p>
              </div>
              <div>
                <p className="text-xs text-teal-700 mb-1">Customer</p>
                <p className="text-sm font-semibold text-teal-900">
                  {transaction.customer || "Walk-in"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-900">
                Items ({transaction.items})
              </h3>
            </div>

            <div className="divide-y divide-gray-200">
              {transaction.itemsList.map((item) => (
                <div
                  key={item.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-sm font-bold text-gray-900">
                        {item.total.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <div className="flex items-center gap-4">
                      <span>Qty: {item.quantity}</span>
                      <span>@ {item.unitPrice.toFixed(2)}</span>
                      <span className="text-red-600 font-medium">
                        Disc: {Number(item.discount || 0).toFixed(2)}
                      </span>
                      <span className="text-teal-700 font-medium">
                        VAT: {Number(item.vat || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-bold text-gray-900 mb-3">
              Payment Summary
            </h3>

            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Subtotal:</span>
                <span className="text-sm font-medium text-gray-900">
                  {transaction.subtotal.toFixed(2)}
                </span>
              </div>

              {totalDiscount > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-red-600">Discount:</span>
                  <span className="text-sm font-medium text-red-600">
                    -{totalDiscount.toFixed(2)}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">VAT Total:</span>
                <span className="text-sm font-medium text-gray-900">
                  {effectiveVat.toFixed(2)}
                </span>
              </div>

              {paymentMethods.map((p, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center py-2 border-b border-gray-100"
                >
                  <span className="text-sm text-gray-600">{p.method}:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {p.amount.toFixed(2)}
                  </span>
                </div>
              ))}

              <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                <span className="text-base font-bold text-gray-900">
                  Total:
                </span>
                <span className="text-xl font-bold text-teal-600">
                  {transaction.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <button
            onClick={handlePrint}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-printer-line text-base"></i>
            Print Receipt
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>

      {/* Hidden receipt for Sunmi thermal printing */}
      <div
        ref={receiptRef}
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "302px",
          backgroundColor: "#ffffff",
          color: "#000",
        }}
      >
        <div
          style={{
            fontFamily: "'Arial', 'Helvetica Neue', sans-serif",
            fontSize: "13px",
            lineHeight: "1.5",
            color: "#000",
            paddingLeft: "8px",
            paddingRight: "8px",
          }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", margin: 0, paddingTop: "6px" }}>
            <div
              style={{
                overflow: "hidden",
                height: "65px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={logoUrl ?? "/golfer_logo.jpg"}
                alt="Golfer"
                style={{
                  width: "280px",
                  height: "200px",
                  objectFit: "contain",
                  display: "block",
                  flexShrink: 0,
                }}
              />
            </div>
          </div>

          {/* Outlet name */}
          {outletName && (
            <>
              <div
                style={{
                  textAlign: "center",
                  fontSize: "13px",
                  margin: "2px 0",
                  color: "#666",
                }}
              >
                Outlet Name: {outletName}
              </div>
              <div
                style={{
                  height: "1px",
                  background:
                    "repeating-linear-gradient(90deg, #ccc 0, #ccc 4px, transparent 4px, transparent 7px)",
                  margin: "4px 0",
                  border: "none",
                }}
              />
            </>
          )}

          {/* User / Invoice / Date */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "12px",
              ...blk,
            }}
          >
            <tbody>
              <tr>
                <td style={blk}>User: {cashier}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap", ...blk }}>
                  Inv. No.: {orderNo}
                </td>
              </tr>
              <tr>
                <td style={blk}>
                  Date: {dateShort}&nbsp;&nbsp;&nbsp;{timeStr}
                </td>
                <td style={{ textAlign: "right", ...blk }}></td>
              </tr>
            </tbody>
          </table>
          <div style={{ fontSize: "12px", ...blk }}>
            Total number of Product: {lineBreakdown.length}
          </div>
          <div style={{ fontSize: "12px", ...blk }}>
            Total quantity of Product: {totalQty}
          </div>
          <div style={{ height: "2px", background: "#000", margin: "4px 0" }} />

          {/* Items table */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
              ...blk,
            }}
          >
            <colgroup>
              <col style={{ width: "16px" }} />
              <col />
              <col style={{ width: "62px" }} />
              <col style={{ width: "22px" }} />
              <col style={{ width: "64px" }} />
            </colgroup>
            <thead>
              <tr
                style={{
                  borderTop: "1px solid #000",
                  borderBottom: "1px solid #000",
                }}
              >
                {(["Sl.", "Product", "Price", "Qty", "Amount"] as const).map(
                  (h, i) => (
                    <th
                      key={h}
                      style={{
                        textAlign:
                          i === 0 || i === 1
                            ? "left"
                            : i === 3
                              ? "center"
                              : "right",
                        padding: "3px 2px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        ...blk,
                      }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {lineBreakdown.map((item, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      padding: "3px 2px 3px 0",
                      verticalAlign: "top",
                      fontSize: "12px",
                      fontWeight: "bold",
                      ...blk,
                    }}
                  >
                    {idx + 1}
                  </td>
                  <td
                    style={{ padding: "3px 2px", verticalAlign: "top", ...blk }}
                  >
                    <div style={{ fontSize: "12px", ...blk }}>
                      {item.isBogoFreeItem ? "(Promo) " : ""}
                      {item.name}
                    </div>
                    {item.code && (
                      <div style={{ fontSize: "11px", ...blk }}>
                        SKU: {item.code}
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      padding: "3px 2px",
                      verticalAlign: "top",
                      fontSize: "12px",
                      ...blk,
                    }}
                  >
                    {fmt(item.price)}
                  </td>
                  <td
                    style={{
                      textAlign: "center",
                      padding: "3px 2px",
                      verticalAlign: "top",
                      fontSize: "12px",
                      ...blk,
                    }}
                  >
                    {item.qty}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      padding: "3px 0 3px 2px",
                      verticalAlign: "top",
                      fontSize: "12px",
                      ...blk,
                    }}
                  >
                    {fmt(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ height: "2px", background: "#000", margin: "4px 0" }} />

          {/* Totals */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
              fontWeight: "bold",
              ...blk,
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    padding: "2px 0",
                    paddingLeft: "55px",
                    fontWeight: "bold",
                    ...blk,
                  }}
                >
                  Sub Total
                </td>
                <td
                  style={{
                    textAlign: "right",
                    padding: "2px 0",
                    fontWeight: "bold",
                    ...blk,
                  }}
                >
                  {bizCurrency}&nbsp;&nbsp;{fmt(subtotal)}
                </td>
              </tr>
              <tr style={{ fontWeight: "normal" }}>
                <td
                  style={{ padding: "2px 0", paddingLeft: "55px", ...blk }}
                >
                  (-) Discount
                </td>
                <td style={{ textAlign: "right", padding: "2px 0", ...blk }}>
                  {bizCurrency}&nbsp;&nbsp;{fmt(totalDiscount)}
                </td>
              </tr>
              <tr style={{ fontWeight: "normal" }}>
                <td
                  style={{ padding: "2px 0", paddingLeft: "55px", ...blk }}
                >
                  (+) VAT
                </td>
                <td style={{ textAlign: "right", padding: "2px 0", ...blk }}>
                  {bizCurrency}&nbsp;&nbsp;{fmt(effectiveVat)}
                </td>
              </tr>
            </tbody>
          </table>
          {(showNetPayable || showRounding) && (
            <div
              style={{
                height: "1px",
                background:
                  "repeating-linear-gradient(90deg, #ccc 0, #ccc 4px, transparent 4px, transparent 7px)",
                margin: "4px 0",
                border: "none",
              }}
            />
          )}
          {(showNetPayable || showRounding) && (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
                fontWeight: "bold",
                ...blk,
              }}
            >
              <tbody>
                {showNetPayable && (
                  <tr>
                    <td
                      style={{
                        padding: "2px 0",
                        paddingLeft: "55px",
                        ...blk,
                      }}
                    >
                      Net Payable
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        padding: "2px 0",
                        ...blk,
                      }}
                    >
                      {bizCurrency}&nbsp;&nbsp;{fmt(netPayable)}
                    </td>
                  </tr>
                )}
                {showRounding && (
                  <tr>
                    <td
                      style={{
                        padding: "2px 0",
                        paddingLeft: "55px",
                        ...blk,
                      }}
                    >
                      Rounding (+/-)
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        padding: "2px 0",
                        ...blk,
                      }}
                    >
                      {bizCurrency}&nbsp;&nbsp;{fmt(rounding)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          <div
            style={{
              height: "1px",
              background:
                "repeating-linear-gradient(90deg, #ccc 0, #ccc 4px, transparent 4px, transparent 7px)",
              margin: "4px 0",
              border: "none",
            }}
          />
          <table style={{ width: "100%", borderCollapse: "collapse", ...blk }}>
            <tbody>
              <tr>
                <td
                  style={{
                    fontWeight: "bold",
                    fontSize: "13px",
                    padding: "3px 0",
                    paddingLeft: "55px",
                    ...blk,
                  }}
                >
                  NET DUE
                </td>
                <td
                  style={{
                    textAlign: "right",
                    fontWeight: "bold",
                    fontSize: "13px",
                    padding: "3px 0",
                    ...blk,
                  }}
                >
                  {bizCurrency}&nbsp;&nbsp;{fmt(total)}
                </td>
              </tr>
            </tbody>
          </table>
          {/* Payment Breakdown */}
          <div
            style={{
              fontWeight: "bold",
              fontSize: "13px",
              margin: "6px 0 2px",
              ...blk,
            }}
          >
            PAYMENT BREAKDOWN
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
              ...blk,
            }}
          >
            <tbody>
              {paymentMethods.map((pm, idx) => (
                <tr key={idx}>
                  <td
                    style={{ padding: "2px 0", paddingLeft: "55px", ...blk }}
                  >
                    Paid ({pm.method})
                  </td>
                  <td
                    style={{ textAlign: "right", padding: "2px 0", ...blk }}
                  >
                    {bizCurrency}&nbsp;&nbsp;{fmt(pm.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              height: "1px",
              background:
                "repeating-linear-gradient(90deg, #ccc 0, #ccc 4px, transparent 4px, transparent 7px)",
              margin: "4px 0",
              border: "none",
            }}
          />
          <table style={{ width: "100%", borderCollapse: "collapse", ...blk }}>
            <tbody>
              <tr>
                <td
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    padding: "3px 0",
                    paddingLeft: "55px",
                    ...blk,
                  }}
                >
                  Change
                </td>
                <td
                  style={{
                    textAlign: "right",
                    fontWeight: "bold",
                    fontSize: "14px",
                    padding: "3px 0",
                    ...blk,
                  }}
                >
                  {bizCurrency}&nbsp;&nbsp;{fmt(change)}
                </td>
              </tr>
            </tbody>
          </table>
          <div style={{ height: "2px", background: "#000", margin: "4px 0" }} />

          {/* Transaction Details */}
          <div
            style={{
              fontWeight: "bold",
              fontSize: "13px",
              marginBottom: "2px",
              ...blk,
            }}
          >
            Transaction Details
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "12px",
              ...blk,
            }}
          >
            <tbody>
              {paymentMethods
                .filter((pm) => pm.amount > 0)
                .map((pm, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #ccc" }}>
                    <td style={{ padding: "2px 0", ...blk }}>
                      {idx + 1}. {pm.method.toUpperCase()}
                    </td>
                    <td
                      style={{ textAlign: "right", padding: "2px 0", ...blk }}
                    >
                      {bizCurrency} {fmt(pm.amount)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {/* Payment Total */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
              ...blk,
            }}
          >
            <tbody>
              <tr>
                <td style={{ fontWeight: "bold", padding: "2px 0", ...blk }}>
                  PAYMENT TOTAL
                </td>
                <td
                  style={{
                    textAlign: "right",
                    fontWeight: "bold",
                    padding: "2px 0",
                    ...blk,
                  }}
                >
                  {bizCurrency} {fmt(totalPaid)}
                </td>
              </tr>
            </tbody>
          </table>
          <div
            style={{ height: "2px", background: "#000", margin: "4px 0" }}
          />

          {/* Customer */}
          <div style={{ fontSize: "12px", ...blk }}>
            Customer: {customerName}
          </div>
          <div
            style={{
              height: "1px",
              background:
                "repeating-linear-gradient(90deg, #ccc 0, #ccc 4px, transparent 4px, transparent 7px)",
              margin: "4px 0",
              border: "none",
            }}
          />

          {/* Terms & Conditions */}
          <div
            style={{
              fontWeight: "bold",
              fontSize: "12px",
              marginBottom: "2px",
              ...blk,
            }}
          >
            Terms &amp; Conditions:
          </div>
          <div style={{ fontSize: "11px", ...blk }}>
            1. Products exchangeable within 15 days of purchase.
          </div>
          <div style={{ fontSize: "11px", ...blk }}>
            2. Exchanged products cannot be re-exchanged.
          </div>
          <div style={{ fontSize: "11px", ...blk }}>
            3. No refund in any form is allowed.
          </div>
          <div
            style={{
              height: "1px",
              background:
                "repeating-linear-gradient(90deg, #ccc 0, #ccc 4px, transparent 4px, transparent 7px)",
              margin: "4px 0",
              border: "none",
            }}
          />

          {/* Barcode */}
          <div
            style={{
              textAlign: "center",
              margin: "8px 0 2px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: "11px", marginBottom: "4px", ...blk }}>
              Scan for Exchange / Tracking
            </div>
            <Barcode
              value={orderNo || "0000000000"}
              format="CODE128"
              width={1.2}
              height={50}
              displayValue={false}
              margin={0}
              lineColor="#000"
              background="#fff"
            />
            <div style={{ fontSize: "11px", marginTop: "2px", ...blk }}>
              {orderNo}
            </div>
          </div>
          <div style={{ height: "2px", background: "#000", margin: "4px 0" }} />

          {/* Footer */}
          <div
            style={{
              textAlign: "center",
              fontSize: "11px",
              marginTop: "4px",
              lineHeight: "1.7",
              ...blk,
            }}
          >
            {storeAddress && <div style={blk}>{storeAddress}</div>}
            {storePhone && <div style={blk}>Tel: {storePhone}</div>}
            {bizFacebook && <div style={blk}>{bizFacebook}</div>}
            <div style={{ marginTop: "3px", ...blk }}>
              <div style={blk}>Thank you for shopping with {storeName}!</div>
              <div style={{ fontWeight: "bold", ...blk }}>Customer Copy</div>
            </div>
          </div>
          <div
            style={{
              textAlign: "right",
              fontSize: "10px",
              marginTop: "6px",
              ...blk,
            }}
          >
            Powered by biznify.net
          </div>
        </div>
      </div>
    </div>
  );
}
